import { useEffect, useState, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { Clock, Eye, Users, Zap, Crown, Flame, Sparkles, } from "lucide-react";

import { db } from "../firebase";
import { fetchBroadcastsForCreators } from "../services/broadcastService";
import { fetchUsersByIds } from "../services/userQueryService";
import { useAuth } from "../context/AuthContext";
import ProfileAvatarLink from "../components/profile/ProfileAvatarLink";
import ProfileNameLink from "../components/profile/ProfileNameLink";
import SafeImage from "../components/SafeImage";
import SkeletonCard from "../components/SkeletonCard";
import MomentLightbox from "../components/MomentLightbox";

function timeSince(ts) {
  if (!ts?.seconds) return "";
  const diff = Date.now() - ts.seconds * 1000;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

function useCountdown(date, time) {
  const [label, setLabel] = useState("");
  useEffect(() => {
    const calc = () => {
      const diff = new Date(`${date}T${time || "00:00"}`).getTime() - Date.now();
      if (diff <= 0) { setLabel("Live now"); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setLabel(d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    calc();
    const t = setInterval(calc, 60000);
    return () => clearInterval(t);
  }, [date, time]);
  return label;
}

/* ── QUICK FILTER PILLS ── */
function FilterPills({ active, onChange, counts, visible, static: isStatic }) {
  const pills = [
    { id: "all",        emoji: "✨", label: "All",            icon: "M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" },
    { id: "closing",    emoji: "🔥", label: "Closing Fast",   icon: "M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z",   count: counts.closing,    color: "#ef4444" },
    { id: "drops",      emoji: "⚡", label: "Drops",          icon: "M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z",                                                                                    count: counts.drops,      color: "#8b5cf6" },
    { id: "broadcasts", emoji: "📣", label: "Broadcasts",     icon: "M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z", count: counts.broadcasts, color: "#ec4899" },
    { id: "exclusive",  emoji: "👑", label: "Exclusive",      icon: "M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0l4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0l-5.571 3-5.571-3", count: counts.exclusive, color: "#f59e0b" },
  ];

  if (isStatic) {
    return (
      <div className="ff-pills-wrap ff-pills-static">
        <div className="ff-pills-scroll ff-pills-text-scroll">
          {pills.map(p => (
            <button
              key={p.id}
              type="button"
              className={`ff-text-pill ${active === p.id ? "ff-text-pill-active" : ""}`}
              onClick={() => onChange(p.id)}
            >
              <span>{p.emoji}</span>
              {p.label}
              {p.count > 0 && <span className="ff-text-pill-count">{p.count}</span>}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`ff-pills-wrap ff-pills-floating ${visible ? "ff-pills-visible" : "ff-pills-hidden"}`}>
      <div className="ff-pills-scroll">
        {pills.map(p => (
          <button
            key={p.id}
            type="button"
            className={`ff-pill-icon ${active === p.id ? "ff-pill-icon-active" : ""}`}
            onClick={() => onChange(p.id)}
            title={p.label}
            style={active === p.id && p.color ? { background: p.color, borderColor: p.color } : {}}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d={p.icon} />
            </svg>
            {p.count > 0 && <span className="ff-pill-icon-count">{p.count}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── STORY RINGS ── */
function StoryRings({ creators }) {
  return (
    <div className="ff-rings-wrap">
      <div className="ff-rings-scroll">
        {creators.map(c => (
          <div key={c.id} className="ff-ring-item">
            <div className={`ff-ring ${c.hasNew ? "ff-ring-active" : "ff-ring-seen"}`}>
              <ProfileAvatarLink
                user={c}
                src={c.avatar || c.avatarUrl || ""}
                alt={c.displayName || c.username}
                className="ff-ring-av-link"
                imgClassName="ff-ring-img"
              />
              {c.hasNew && <div className="ff-ring-new-dot" />}
            </div>
            <ProfileNameLink user={c} className="ff-ring-name">
              {(c.displayName || c.username).split(" ")[0]}
            </ProfileNameLink>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── DROP STRIP ── */
function DropCard({ drop }) {
  const countdown = useCountdown(drop.date, drop.time);
  const diff = new Date(`${drop.date}T${drop.time || "00:00"}`).getTime() - Date.now();
  const isUrgent = diff < 86400000 * 2;

  return (
    <Link to={`/drop/${drop.id || drop.dropId}`} className={`ff-drop-card ${isUrgent ? "ff-drop-urgent" : ""}`}>
      <div className="ff-drop-card-top">
        <ProfileAvatarLink
          user={{ handle: drop.creatorUsername, username: drop.creatorUsername, isCreator: true }}
          isCreator
          handle={drop.creatorUsername}
          src={drop.creatorAvatar || ""}
          alt={drop.creatorDisplayName}
          className="ff-drop-card-avatar-link"
          imgClassName="ff-drop-card-avatar"
          onClick={(e) => e.stopPropagation()}
        />
        <div className={`ff-drop-card-countdown ${isUrgent ? "urgent" : ""}`}>
          <Clock size={10} strokeWidth={2.5} />
          {countdown}
        </div>
      </div>
      <p className="ff-drop-card-title">{drop.title}</p>
      <div className="ff-drop-card-type">
        {drop.dropType === "supporter_only"
          ? <><Crown size={9} strokeWidth={2.5} />Supporters</>
          : <><Zap size={9} strokeWidth={2.5} />Limited</>}
      </div>
    </Link>
  );
}

/* ── BROADCAST CARD (full width, speech bubble) ── */
function BroadcastCard({ broadcast }) {
  const creatorUser = {
    handle: broadcast.creatorUsername,
    username: broadcast.creatorUsername,
    isCreator: true,
  };

  return (
    <div className="ff-broadcast-bubble">
      <ProfileAvatarLink
        user={creatorUser}
        isCreator
        handle={broadcast.creatorUsername}
        src={broadcast.creatorAvatar || ""}
        alt={broadcast.creatorDisplayName}
        className="ff-bc-avatar-link"
        imgClassName="ff-bc-avatar"
      />
      <Link to={`/creator/${broadcast.creatorUsername}`} className="ff-bc-content">
        <div className="ff-bc-header">
          <ProfileNameLink user={creatorUser} className="ff-bc-creator-name">
            {broadcast.creatorDisplayName}
          </ProfileNameLink>
          <span>{timeSince(broadcast.createdAt)}</span>
        </div>
        <p>{broadcast.message}</p>
      </Link>
    </div>
  );
}

/* ── FULL WIDTH MOMENT CARD ── */
function MomentCard({ moment, onOpen }) {
  const left = Math.max((moment.freeViewLimit || 100) - (moment.viewCount || 0), 0);
  const isLimited = moment.accessType === "limited";
  const isLocked = isLimited && left <= 0;
  const isAlmost = isLimited && left > 0 && left <= 15;
  const isSupOnly = moment.accessType === "supporter_only";
  const pct = Math.min(100, (left / (moment.freeViewLimit || 100)) * 100);
  const barColor = pct > 60 ? "#22c55e" : pct > 30 ? "#f59e0b" : "#ef4444";

  return (
    <div className="ff-moment-full" onClick={onOpen}>
      <div className="ff-moment-img-wrap">
        <SafeImage
          src={moment.mediaUrl}
          alt={moment.caption}
          className={`ff-moment-img ${isLocked ? "blurred-image" : ""}`}
          fallbackText=""
        />

        {/* Gradient overlay */}
        <div className="ff-moment-gradient" />

        {/* Urgency badge */}
        {isAlmost && (
          <div className="ff-moment-badge urgent">
            <Flame size={11} strokeWidth={2.5} />
            {left} views left
          </div>
        )}
        {isSupOnly && (
          <div className="ff-moment-badge exclusive">
            <Crown size={11} strokeWidth={2.5} />
            Exclusive
          </div>
        )}
        {isLocked && (
          <div className="ff-moment-locked-overlay">
            <Crown size={22} strokeWidth={1.5} color="white" />
            <span>Free window closed</span>
          </div>
        )}

        {/* Creator + caption overlaid at bottom */}
        <div className="ff-moment-info">
          <div className="ff-moment-creator">
            <ProfileAvatarLink
              user={{ handle: moment.creatorUsername, username: moment.creatorUsername, isCreator: true }}
              isCreator
              handle={moment.creatorUsername}
              src={moment.creatorAvatar || ""}
              alt={moment.creatorDisplayName}
              className="ff-moment-creator-avatar-link"
              imgClassName="ff-moment-creator-avatar"
              onClick={(e) => e.stopPropagation()}
            />
            <div>
              <ProfileNameLink
                user={{ handle: moment.creatorUsername, username: moment.creatorUsername, isCreator: true }}
                className="ff-moment-creator-name"
                onClick={(e) => e.stopPropagation()}
              >
                {moment.creatorDisplayName}
              </ProfileNameLink>
              <span>{timeSince(moment.createdAt)}</span>
            </div>
          </div>
          {moment.caption && <p className="ff-moment-caption-text">{moment.caption}</p>}

          {/* Progress bar */}
          {isLimited && !isLocked && (
            <div className="ff-moment-progress">
              <div className="ff-moment-progress-fill" style={{ width: `${pct}%`, background: barColor }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── MAIN ── */
export default function FollowingFeed() {
  const { currentUser } = useAuth();
  const [creatorRings, setCreatorRings] = useState([]);
  const [moments, setMoments] = useState([]);
  const [drops, setDrops] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [pillsVisible, setPillsVisible] = useState(false);
  const lastScrollY = useRef(0);

  const dropsRef = useRef(null);
  const closingRef = useRef(null);
  const broadcastsRef = useRef(null);
  const exclusiveRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      if (!currentUser) { setLoading(false); return; }
      try {
        const followsSnap = await getDocs(query(collection(db, "follows"), where("followerId", "==", currentUser.uid)));
        const creatorIds = followsSnap.docs.map(d => d.data().creatorId).filter(Boolean);
        if (creatorIds.length === 0) { setLoading(false); return; }

        const [followedUsers, momentsSnap, dropsSnap, allBroadcasts] = await Promise.all([
          fetchUsersByIds(creatorIds),
          getDocs(collection(db, "moments")),
          getDocs(collection(db, "drops")),
          fetchBroadcastsForCreators(creatorIds),
        ]);
        const usersById = Object.fromEntries(followedUsers.map((u) => [u.id, u]));

        const allMoments = momentsSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(m => creatorIds.includes(m.creatorId))
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

        const recentBroadcasts = allBroadcasts.slice(0, 6);

        const allDrops = dropsSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(d => creatorIds.includes(d.creatorId) && new Date(`${d.date}T${d.time || "00:00"}`).getTime() > Date.now())
          .sort((a, b) => new Date(`${a.date}T${a.time || "00:00"}`).getTime() - new Date(`${b.date}T${b.time || "00:00"}`).getTime())
          .slice(0, 8);

        const seenIds = [...new Set(allMoments.slice(0, 24).map(m => m.creatorId))];
        const rings = seenIds.map((cid) => {
          const u = usersById[cid];
          if (!u) return null;
          const recent = allMoments.find(m => m.creatorId === cid);
          const hasNew = recent?.createdAt?.seconds ? Date.now() - recent.createdAt.seconds * 1000 < 86400000 * 3 : false;
          return { id: cid, username: u.username, displayName: u.displayName, avatar: u.avatar || u.avatarUrl, hasNew };
        }).filter(Boolean).slice(0, 14);

        setCreatorRings(rings);
        setMoments(allMoments);
        setDrops(allDrops);
        setBroadcasts(recentBroadcasts);
      } catch (err) {
        console.error("Following feed error:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  // Scroll to section when pill tapped
  useEffect(() => {
    if (activeFilter === "all") return;
    const refs = { drops: dropsRef, closing: closingRef, broadcasts: broadcastsRef, exclusive: exclusiveRef };
    const ref = refs[activeFilter];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeFilter]);


  // Only show pills after scrolling past the closing fast section
  // Hide when scrolling down, show when scrolling up — but never above threshold
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;

      // Find how far down the closing section is
      const threshold = closingRef.current
        ? closingRef.current.offsetTop + closingRef.current.offsetHeight
        : 300;

      if (currentY < threshold) {
        // Above the closing section — always hide
        setPillsVisible(false);
      } else if (currentY > lastScrollY.current + 8) {
        // Scrolling down past threshold — hide
        setPillsVisible(false);
      } else if (currentY < lastScrollY.current - 8) {
        // Scrolling up past threshold — show
        setPillsVisible(true);
      }

      lastScrollY.current = currentY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!currentUser) return <Navigate to="/welcome" replace />;

  if (loading) return <div className="page"><SkeletonCard /><SkeletonCard /></div>;

  const isEmpty = moments.length === 0 && drops.length === 0;

  // Counts for pills
  const closingMoments = moments.filter(m => {
    const left = Math.max((m.freeViewLimit || 100) - (m.viewCount || 0), 0);
    return m.accessType === "limited" && left > 0 && left <= 15;
  });
  const exclusiveMoments = moments.filter(m => m.accessType === "supporter_only");

  const counts = {
    closing: closingMoments.length,
    drops: drops.length,
    broadcasts: broadcasts.length,
    exclusive: exclusiveMoments.length,
  };

  // Build filtered feed
  const getFilteredMoments = () => {
    if (activeFilter === "closing") return closingMoments;
    if (activeFilter === "exclusive") return exclusiveMoments;
    if (activeFilter === "broadcasts") return [];
    return moments;
  };

  const filteredMoments = getFilteredMoments();

  // Build mixed feed with broadcasts injected every 4 moments
  const buildMixedFeed = (momentList) => {
    if (activeFilter === "broadcasts") {
      return broadcasts.map(b => ({ type: "broadcast", data: b }));
    }
    const feed = [];
    let bIdx = 0;
    momentList.forEach((m, i) => {
      feed.push({ type: "moment", data: m });
      if ((i + 1) % 4 === 0 && bIdx < broadcasts.length && activeFilter === "all") {
        feed.push({ type: "broadcast", data: broadcasts[bIdx++] });
      }
    });
    return feed;
  };

  const mixedFeed = buildMixedFeed(filteredMoments);

  return (
    <div className="ff-page">

      {/* Header */}
      <div className="ff-header">
        <div>
          <p className="exact-eyebrow">Following</p>
          <h1 className="ff-title-gradient">Your creator circle</h1>
        </div>
        <Link to="/discover" className="ff-discover-btn">
          <Users size={13} strokeWidth={2.5} />
          Discover more
        </Link>
      </div>

      {/* Story rings */}
      {creatorRings.length > 0 && <StoryRings creators={creatorRings} />}

      {isEmpty ? (
        <div className="ff-empty">
          <div className="ff-empty-icon"><Users size={36} strokeWidth={1.5} color="var(--light-muted)" /></div>
          <strong>Nothing here yet</strong>
          <p>The creators you follow will show up here as they post moments and drops.</p>
          <Link to="/discover" className="exact-gradient-button">Browse creators</Link>
        </div>
      ) : (
        <>
          {/* Static pills at top of page — always visible */}
          <FilterPills active={activeFilter} onChange={setActiveFilter} counts={counts} visible={true} static />

          {/* Floating pills — appear after scrolling past closing section */}
          <FilterPills active={activeFilter} onChange={setActiveFilter} counts={counts} visible={pillsVisible} />

          {/* Drop strip */}
          {drops.length > 0 && (activeFilter === "all" || activeFilter === "drops") && (
            <div className="ff-drops-section" ref={dropsRef}>
              <div className="ff-section-label">
                <Sparkles size={14} strokeWidth={2.5} color="var(--purple)" />
                <strong>Dropping soon</strong>
                <span>{drops.length} upcoming</span>
              </div>
              <div className="ff-drops-scroll">
                {drops.map(drop => <DropCard key={drop.id} drop={drop} />)}
              </div>
            </div>
          )}

          {/* Closing fast alert strip */}
          {closingMoments.length > 0 && (activeFilter === "all" || activeFilter === "closing") && (
            <div className="ff-closing-section" ref={closingRef}>
              <div className="ff-section-label urgent">
                <Flame size={14} strokeWidth={2.5} color="#ef4444" />
                <strong>Closing fast</strong>
                <span>{closingMoments.length} almost gone</span>
                <div className="ff-closing-pulse" />
              </div>
              <div className="ff-closing-strip">
                {closingMoments.slice(0, 4).map((m, i) => {
                  const left = Math.max((m.freeViewLimit || 100) - (m.viewCount || 0), 0);
                  return (
                    <div
                      key={m.id}
                      className="ff-closing-chip"
                      onClick={() => {
                        const idx = moments.findIndex(x => x.id === m.id);
                        setLightboxIndex(idx >= 0 ? idx : 0);
                      }}
                    >
                      <div className="ff-closing-chip-img">
                        <SafeImage src={m.mediaUrl} alt={m.caption} className="ff-chip-img" fallbackText="" />
                        <div className="ff-chip-count">
                          <Eye size={9} strokeWidth={2.5} />
                          {left}
                        </div>
                      </div>
                      <span>{m.creatorDisplayName}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Main feed */}
          <div className="ff-feed" ref={activeFilter === "exclusive" ? exclusiveRef : activeFilter === "broadcasts" ? broadcastsRef : null}>
            {mixedFeed.length === 0 ? (
              <div className="ff-filter-empty">
                <p>Nothing here right now.</p>
              </div>
            ) : (
              mixedFeed.map((item, i) =>
                item.type === "broadcast"
                  ? <BroadcastCard key={`b-${item.data.id}`} broadcast={item.data} />
                  : <MomentCard
                      key={`m-${item.data.id}`}
                      moment={item.data}
                      onOpen={() => {
                        const idx = moments.findIndex(m => m.id === item.data.id);
                        setLightboxIndex(idx >= 0 ? idx : 0);
                      }}
                    />
              )
            )}
          </div>
        </>
      )}

      {lightboxIndex !== null && (
        <MomentLightbox
          moments={moments}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}